import apiClient from '@/lib/api-client';
import { ApiError } from '@/model/ApiError';
import Package from '@/model/enums/Package';
import {
    EventResponse,
    InviteUserRequest,
    RemoveUserRequest,
} from '@/model/Event';
import { UpdateForm } from '@/model/form/UpdateForm';
import {
    InviteRequest,
    Project,
    ProjectIdAndUserId,
    ProjectUser,
} from '@/model/Project';
import router from '@/router';
import { Module, Mutation, VuexModule, Action } from 'vuex-module-decorators';

@Module({ namespaced: true, preserveState: true })
export default class CustomerProjectStorage extends VuexModule {
    projects: Project[] = new Array<Project>();

    project: Project | null = null;

    projectUsers: ProjectUser[] = new Array<ProjectUser>();

    error: ApiError | null = null;

    projectEvents: EventResponse[] = new Array<EventResponse>();

    @Mutation
    setProjectPackage(request: UpdateForm<Package>): void {
        this.projects.forEach((project) => {
            if (project.id === request.id) {
                // eslint-disable-next-line no-param-reassign
                project.projectPackage = request.formData;
            }
        });
    }

    @Mutation
    addProjectUserEventId(
        request: InviteUserRequest | RemoveUserRequest,
    ): void {
        this.projectUsers
            .find((projectUser) => projectUser.id === request.userId)
            ?.eventIds.push(request.eventId);
    }

    @Mutation
    removeProjectUserEventId(
        request: InviteUserRequest | RemoveUserRequest,
    ): void {
        const index = this.projectUsers.findIndex(
            (user) => user.id === request.userId,
        );
        const eventIdIndex = this.projectUsers[index].eventIds.findIndex(
            (id) => id === request.eventId,
        );
        this.projectUsers[index].eventIds.splice(eventIdIndex, 1);
    }

    @Mutation
    setProjects(newProjects: Project[]): void {
        this.projects = newProjects;
    }

    @Mutation
    setProject(project: Project): void {
        this.project = project;
    }

    @Mutation
    setError(newError: ApiError | null): void {
        this.error = newError;
    }

    @Mutation
    setProjectUsers(newProjectUsers: ProjectUser[]): void {
        newProjectUsers?.forEach((projectUser: ProjectUser) => {
            projectUser.name = `${projectUser.firstName} ${projectUser.lastName}`;
        });
        this.projectUsers = newProjectUsers;
    }

    @Mutation
    deleteUserFromProject(id: number): void {
        this.projectUsers = this.projectUsers.filter(
            (projectUsers) => projectUsers.id !== id,
        );
    }

    @Mutation
    setProjectEvents(events: EventResponse[]): void {
        this.projectEvents = events;
    }

    @Action
    getAllProjectUsers(id: number): Promise<void | ProjectUser[]> {
        return apiClient
            .get<ProjectUser[]>(`/projects/${id}/users`)
            .then((data) => {
                this.context.commit('setProjectUsers', data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    @Action
    fetchProject(projectId: number): Promise<void> {
        return apiClient
            .get(`/projects/${projectId}`)
            .then((data) => {
                this.context.commit('setProject', data);
            })
            .catch((error) => {
                if (error?.key === 'security.permission.denied') {
                    router.push({ name: 'NotFound' });
                }

                this.context.commit('setProjectUsers', null);
                return Promise.reject(error);
            });
    }

    @Action
    fetchProjectEvents(projectId: number): Promise<void | EventResponse[]> {
        return apiClient
            .get<void, EventResponse[]>(`/projects/${projectId}/events`)
            .then((data: EventResponse[]) => {
                this.context.commit('setProjectEvents', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    getAllProjects(onlyActives: boolean): Promise<void> {
        return apiClient
            .get('/projects', {
                params: { onlyActives },
            })
            .then((data) => {
                this.context.commit('setProjects', data);
            })
            .catch((error) => {
                this.context.commit('setProjectUsers', null);
                return Promise.reject(error);
            });
    }

    @Action
    deactivate(id: number): Promise<void> {
        return apiClient
            .delete(`/projects/${id}`)
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    reactivate(id: number): Promise<void> {
        return apiClient
            .put(`/projects/${id}`)
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    invite(request: InviteRequest): Promise<void> {
        return apiClient
            .post(`/projects/${request.projectId}/invite`, {
                email: request.email,
                eventIds: request.eventIds,
            })
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action({ commit: 'deleteUserFromProject' })
    removeUserFromProject(request: ProjectIdAndUserId): Promise<void | number> {
        return apiClient
            .delete(`/projects/${request.projectId}/users/${request.userId}`)
            .then(() => {
                this.context.commit('setError', null);
                return request.userId;
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    changeRole(request: {
        userId: number;
        projectId: number;
        role: string;
    }): Promise<void> {
        return apiClient
            .put(`/projects/${request.projectId}/users/${request.userId}`, {
                data: request.role,
            })
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    updatePackage(request: UpdateForm<Package>): Promise<void> {
        return apiClient
            .put(`/projects/${request.id}/package`, {
                data: request.formData,
            })
            .then(() => {
                this.context.commit('setError', null);
                this.context.commit('setProjectPackage', request);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    get getProjects(): Project[] {
        return this.projects;
    }

    get getProject(): Project | null {
        return this.project;
    }

    get getError(): ApiError | null {
        return this.error;
    }

    get getProjectUsers(): ProjectUser[] {
        return this.projectUsers;
    }

    get getProjectEvents(): EventResponse[] {
        return this.projectEvents;
    }
}
